/**
 * TokenService provides methods for managing JWT and refresh tokens in local storage,
 * as well as updating them from HTTP response headers.
 */
export class TokenService {
    private static readonly jwtKey = "tk";
    private static readonly refreshTokenKey = "rtk";

    /**
     * Stores the JWT token in local storage.
     * @param {string} token - The JWT token to store.
     */
    public static setJWT(token: string): void {
        localStorage.setItem(this.jwtKey, token);
    }

    /**
     * Retrieves the JWT token from local storage.
     * @returns {string | null} The stored JWT token, or null if not present.
     */
    public static getJWT(): string | null {
        return localStorage.getItem(this.jwtKey);
    }

    /**
     * Removes the JWT token from local storage.
     * @returns {void}
     */
    public static removeJWT(): void {
        localStorage.removeItem(this.jwtKey);
    }

    /**
     * Stores the refresh token in local storage.
     * @param {string} token - The refresh token to store.
     */
    public static setRefreshToken(token: string): void {
        localStorage.setItem(this.refreshTokenKey, token);
    }

    /**
     * Retrieves the refresh token from local storage.
     * @returns {string | null} The stored refresh token, or null if not present.
     */
    public static getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshTokenKey);
    }

    /**
     * Removes the refresh token from local storage.
     * @returns {void}
     */
    public static removeRefreshToken(): void {
        localStorage.removeItem(this.refreshTokenKey);
    }

    /**
     * Updates stored JWT and refresh tokens based on HTTP response headers.
     * If the response includes an 'Authorization' header or 'rtk' header, those values are persisted.
     *
     * @param {Response} response - The HTTP response containing token headers.
     */
    public static updateTokensFromHeaders(response: Response): void {
        if (response.headers.has('Authorization')) {
            const authHeader = response.headers.get('Authorization');
            const token = authHeader?.split(' ')[1] ?? "";
            if (token)
                this.setJWT(token);
        }

        if (response.headers.has('rtk')) {
            const refreshToken = response.headers.get('rtk');
            if (refreshToken)
                this.setRefreshToken(refreshToken);
        }
    }
}